/* *
 * server-status-banner.js
 * Lightweight sticky banner that tells public visitors when the server is offline.
 * No authentication required. Works for all visitors.
 *
 * Behaviour:
 *  - Polls /api/public/snapshot/status every 30 s
 *  - Shows banner after 2 consecutive failures (avoids false positives on first load)
 *  - Hides automatically when server comes back
 *  - Manual dismiss stored in sessionStorage (re-appears if still offline after page reload)
 *  - Distinct messages for offline vs degraded vs db_down states */

const _API =
  window.ASCEND_API_URL ||
  (location.hostname === "localhost" || location.hostname === "127.0.0.1"
    ? "http://localhost:5001/api"
    : "https://api.ascenddraft.com/api");

const STATUS_URL = `${_API}/public/snapshot/status`;
const POLL_INTERVAL_MS = 30_000;
const FAIL_THRESHOLD = 3; // 3 consecutive failures before showing (avoids page-load jitter)
const STARTUP_DELAY_MS = 5_000; // wait 5s after page load before first poll
const DISMISS_KEY = "ascend_status_banner_dismissed_v1";
const BANNER_ID = "ascend-status-banner";

const MESSAGES = {
  offline:
    "⚠️ Our server is temporarily unreachable. Some features may be unavailable.",
  db_down: "⚠️ We're experiencing database issues. Service is degraded.",
  degraded: "ℹ️ Some services are running slow. We're looking into it.",
};

let _failCount = 0;
let _currentStatus = "ok";
let _banner = null;
let _pollTimer = null;

//  ==== Banner DOM ————————————————————————————————————————————————————————————

function _createBanner() {
  const el = document.createElement("div");
  el.id = BANNER_ID;
  el.setAttribute("role", "alert");
  el.setAttribute("aria-live", "polite");
  el.style.cssText = [
    "position: fixed",
    "top: 0",
    "left: 0",
    "right: 0",
    "z-index: 99999",
    "padding: 10px 48px 10px 20px",
    "font-size: 14px",
    "font-weight: 500",
    "font-family: inherit",
    "text-align: center",
    "color: #fff",
    "background: #c0392b",
    "box-shadow: 0 2px 8px rgba(0,0,0,0.25)",
    "transition: transform 0.3s ease, opacity 0.3s ease",
    "display: none",
  ].join(";");

  const msg = document.createElement("span");
  msg.id = `${BANNER_ID}-msg`;
  el.appendChild(msg);

  const closeBtn = document.createElement("button");
  closeBtn.setAttribute("aria-label", "Dismiss notice");
  closeBtn.style.cssText = [
    "position: absolute",
    "right: 12px",
    "top: 50%",
    "transform: translateY(-50%)",
    "background: none",
    "border: none",
    "color: rgba(255,255,255,0.85)",
    "font-size: 18px",
    "cursor: pointer",
    "padding: 4px 6px",
    "line-height: 1",
  ].join(";");
  closeBtn.textContent = "×";
  closeBtn.addEventListener("click", _dismiss);
  el.appendChild(closeBtn);

  document.body.insertAdjacentElement("afterbegin", el);
  _banner = el;
}

//  ==== Show / Hide ———————————————————————————————————————————————————————————

function _show(status) {
  if (!_banner) _createBanner();
  const msg = document.getElementById(`${BANNER_ID}-msg`);
  if (msg) msg.textContent = MESSAGES[status] || MESSAGES.offline;

  // Colour by severity
  const bg = status === "degraded" ? "#d68910" : "#c0392b";
  _banner.style.background = bg;
  _banner.style.display = "block";

  // Push body down so the nav isn't covered
  _adjustBodyPadding(true);
}

function _hide() {
  if (!_banner) return;
  _banner.style.display = "none";
  _adjustBodyPadding(false);
}

function _dismiss() {
  sessionStorage.setItem(DISMISS_KEY, "1");
  _hide();
}

function _adjustBodyPadding(show) {
  if (!_banner) return;
  const h = show ? _banner.offsetHeight : 0;
  const existing = parseInt(document.body.style.paddingTop || "0", 10);
  if (show && existing < h) {
    document.body.style.paddingTop = `${existing + h}px`;
  } else if (!show) {
    const bh = _banner.offsetHeight || 40;
    const next = Math.max(0, existing - bh);
    document.body.style.paddingTop = next ? `${next}px` : "";
  }
}

//  ==== Poll ——————————————————————————————————————————————————————————————————

async function _poll() {
  try {
    const res = await fetch(STATUS_URL, {
      method: "GET",
      credentials: "omit",
      cache: "no-store",
      signal: AbortSignal.timeout(8000),
    });

    const data = res.ok ? await res.json().catch(() => ({})) : {};
    // Server returns `serverStatus` (not `status`) — check both for safety
    const rawStatus = data.serverStatus || data.status;
    const status = res.ok ? rawStatus || "ok" : "offline";

    if (status === "ok" || status === "checking") {
      _failCount = 0;
      if (_currentStatus !== "ok") {
        _currentStatus = "ok";
        _hide();
        // NOTE: do NOT clear the dismiss key here — if the user dismissed
        // the banner during an outage, respect that for the rest of the session.
        // sessionStorage expires naturally on tab/browser close, so they will
        // see the banner again on the next session if the server is still down.
      }
    } else {
      _failCount++;
      _currentStatus = status;
      if (
        _failCount >= FAIL_THRESHOLD &&
        !sessionStorage.getItem(DISMISS_KEY)
      ) {
        _show(status);
      }
    }
  } catch {
    _failCount++;
    _currentStatus = "offline";
    if (_failCount >= FAIL_THRESHOLD && !sessionStorage.getItem(DISMISS_KEY)) {
      _show("offline");
    }
  }
}

//  ==== Init ——————————————————————————————————————————————————————————————————

function _init() {
  // Delay first poll by 5s — avoids false positives from page-load timing jitter
  // (browser DNS resolution, cold connection, JS parse time can all add latency)
  setTimeout(() => {
    _poll();
    _pollTimer = setInterval(_poll, POLL_INTERVAL_MS);
  }, STARTUP_DELAY_MS);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", _init);
} else {
  _init();
}
